import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  CreditCard, Plus, Search, Trash2, X, ChevronDown,
  ChevronUp, ChevronsUpDown, Lock, Calculator, AlertCircle,
  Percent, Hash, FileDown, Send, CheckCircle2, Loader2,
  FileText, Upload, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { SidePanel } from '../components/SidePanel';
import { useSociete } from '../context/SocieteContext';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';
import { PriceInput } from '../components/PriceInput';

// ─── Composant zone PDF ───────────────────────────────────────────────────────

function PdfDropZone({ financementId, docUrl, docNom, onUploaded, onDeleted, readOnly }) {
  const [dragging, setDragging]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Seuls les fichiers PDF sont acceptés');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.uploadFinancementDocument(financementId, fd);
      onUploaded(res.data.document_url, res.data.document_nom);
      toast.success('Document enregistré');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer le document PDF ?')) return;
    try {
      await api.deleteFinancementDocument(financementId);
      onDeleted();
      toast.success('Document supprimé');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5"
        style={{ color: 'var(--text-muted)' }}>
        Document PDF
      </label>

      {docUrl ? (
        // ── Document existant ───────────────────────────────────────────────
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl border"
          style={{ borderColor: '#BFDBFE', background: '#EFF6FF' }}>
          <FileText size={18} style={{ color: '#1D4ED8', flexShrink: 0 }} />
          <span className="flex-1 text-sm font-medium truncate" style={{ color: '#1D4ED8' }}>
            {docNom || 'contrat.pdf'}
          </span>
          <a href={docUrl} target="_blank" rel="noreferrer"
            title="Ouvrir le PDF"
            className="p-1.5 rounded-lg hover:bg-blue-100 transition-colors flex-shrink-0">
            <ExternalLink size={14} style={{ color: '#1D4ED8' }} />
          </a>
          {!readOnly && (
            <button onClick={handleDelete} title="Supprimer le document"
              data-testid="delete-financement-doc"
              className="p-1.5 rounded-lg hover:bg-red-50 transition-colors flex-shrink-0">
              <Trash2 size={14} style={{ color: '#DC2626' }} />
            </button>
          )}
        </div>
      ) : (
        // ── Zone de dépôt ───────────────────────────────────────────────────
        <div
          onDragOver={e => { e.preventDefault(); if (!readOnly) setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => {
            e.preventDefault();
            setDragging(false);
            if (readOnly) return;
            const file = e.dataTransfer.files?.[0];
            if (file) handleFile(file);
          }}
          onClick={() => { if (!readOnly && !uploading) inputRef.current?.click(); }}
          data-testid="pdf-drop-zone"
          className="flex flex-col items-center gap-2 px-4 py-5 rounded-xl border-2 border-dashed cursor-pointer transition-all"
          style={{
            borderColor: dragging ? 'var(--primary-solid)' : 'var(--border)',
            background:  dragging ? 'var(--primary-50)' : '#FAFAFA',
            cursor: readOnly ? 'not-allowed' : 'pointer',
            opacity: readOnly ? 0.5 : 1,
          }}>
          {uploading ? (
            <Loader2 size={22} className="animate-spin" style={{ color: 'var(--primary-solid)' }} />
          ) : (
            <Upload size={20} style={{ color: dragging ? 'var(--primary-solid)' : 'var(--text-muted)' }} />
          )}
          <p className="text-sm" style={{ color: dragging ? 'var(--primary-solid)' : 'var(--text-muted)' }}>
            {uploading ? 'Envoi en cours...' : 'Glissez un PDF ici ou cliquez pour sélectionner'}
          </p>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
          />
        </div>
      )}
    </div>
  );
}




function calcMensualite(capital, duree, tauxAnnuel) {
  if (!capital || !duree) return null;
  if (!tauxAnnuel || tauxAnnuel === 0) return capital / duree;
  const r = tauxAnnuel / 100 / 12;
  return (capital * r) / (1 - Math.pow(1 + r, -duree));
}

function calcCapital(mensualite, duree, tauxAnnuel) {
  if (!mensualite || !duree) return null;
  if (!tauxAnnuel || tauxAnnuel === 0) return mensualite * duree;
  const r = tauxAnnuel / 100 / 12;
  return (mensualite * (1 - Math.pow(1 + r, -duree))) / r;
}

function calcDuree(capital, mensualite, tauxAnnuel) {
  if (!capital || !mensualite) return null;
  if (!tauxAnnuel || tauxAnnuel === 0) return Math.round(capital / mensualite);
  const r = tauxAnnuel / 100 / 12;
  const ratio = (capital * r) / mensualite;
  if (ratio >= 1) return null;
  return Math.round(-Math.log(1 - ratio) / Math.log(1 + r));
}

function calcTaux(capital, mensualite, duree) {
  if (!capital || !mensualite || !duree) return null;
  let r = 0.005;
  for (let i = 0; i < 200; i++) {
    const pow = Math.pow(1 + r, -duree);
    const f = capital * r - mensualite * (1 - pow);
    const df = capital - mensualite * duree * Math.pow(1 + r, -duree - 1);
    if (Math.abs(df) < 1e-12) break;
    const rNew = r - f / df;
    if (Math.abs(rNew - r) < 1e-10) { r = rNew; break; }
    r = Math.max(0, rNew);
  }
  return r * 12 * 100;
}

function fmt(n, decimals = 2) {
  if (n == null || n === '') return '—';
  return Number(n)
    .toLocaleString('fr-FR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    .replace(/[\u00A0\u202F ]/g, '.'); // milliers → point : 10.000,00
}

function fmtDate(s) {
  if (!s) return '—';
  return new Date(s + 'T12:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Constantes ───────────────────────────────────────────────────────────────

const TYPE_COLORS = {
  Leasing: { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
  SNC:     { bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' },
};

const EMPTY_FORM = {
  numero_contrat: '',
  type: 'Leasing',
  entreprise_id: '',
  materiel_ids: [],
  prix_bien: '',
  apport: '',
  apport_type: 'montant',
  apport_snc: '',
  capital_emprunte: '',
  mensualite: '',
  duree: '',
  taux: '',
  date_debut: '',
  date_fin: '',
  notes: '',
  _deduire: 'mensualite', // champ à déduire
};

// ─── StatCard ─────────────────────────────────────────────────────────────────

function StatCard({ title, value, sub, accent, active, onToggle }) {
  return (
    <div
      onClick={onToggle}
      data-testid={onToggle ? 'toggle-en-cours-card' : undefined}
      className={`bg-white rounded-xl p-4 border transition-all ${onToggle ? 'cursor-pointer select-none hover:shadow-md' : ''} ${active ? 'ring-2' : ''}`}
      style={{ borderColor: active ? accent : 'var(--border)', ringColor: accent }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-2xl font-bold" style={{ color: accent || 'var(--text)',  }}>{value}</p>
          <p className="text-xs mt-0.5 font-medium" style={{ color: 'var(--text-muted)' }}>{title}</p>
          {sub && <p className="text-xs mt-0.5" style={{ color: 'var(--text-placeholder)' }}>{sub}</p>}
        </div>
        {onToggle && (
          <div
            className="flex-shrink-0 w-9 h-5 rounded-full relative transition-colors duration-200 mt-0.5"
            style={{ background: active ? accent : 'var(--border)' }}
          >
            <div
              className="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200"
              style={{ transform: active ? 'translateX(18px)' : 'translateX(2px)' }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── TypeBadge ────────────────────────────────────────────────────────────────

function TypeBadge({ type }) {
  const c = TYPE_COLORS[type] || { bg: '#F3F4F6', color: '#6B7280', border: '#E5E7EB' };
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>
      {type}
    </span>
  );
}

// ─── MaterielsMultiSelect ────────────────────────────────────────────────────

function MaterielsMultiSelect({ selected, materiels, onChange, onNavigate }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = materiels.filter(m => {
    const q = search.toLowerCase();
    return !q || (m.marque || '').toLowerCase().includes(q) || (m.modele || '').toLowerCase().includes(q) || (m.numero_serie || '').toLowerCase().includes(q) || (m.numero_parc || '').toLowerCase().includes(q);
  });

  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id]);
  };

  const selectedMats = materiels.filter(m => selected.includes(m.materiel_id));

  return (
    <div className="relative" ref={ref}>
      <div
        onClick={() => setOpen(o => !o)}
        className="w-full min-h-[38px] px-3 py-2 border rounded-lg text-sm cursor-pointer flex flex-wrap gap-1.5 items-center"
        style={{ borderColor: 'var(--border)', background: 'white' }}
        data-testid="materiels-multiselect"
      >
        {selectedMats.length === 0
          ? <span style={{ color: 'var(--text-placeholder)' }}>Sélectionner des matériels...</span>
          : selectedMats.map(m => (
            <span key={m.materiel_id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium"
              style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)', border: '1px solid #BFDBFE' }}>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); if (onNavigate) onNavigate(m.materiel_id); }}
                className="hover:underline"
                title="Ouvrir la fiche matériel"
              >
                {[m.marque, m.modele].filter(Boolean).join(' ') || m.materiel_id}
                {m.numero_serie && <span style={{ opacity: 0.7 }}> · {m.numero_serie}</span>}
              </button>
              <button type="button" onClick={(e) => { e.stopPropagation(); toggle(m.materiel_id); }}
                className="hover:text-red-500 ml-0.5 flex-shrink-0">
                <X size={10} />
              </button>
            </span>
          ))
        }
        <ChevronDown size={14} className={`ml-auto flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          style={{ color: 'var(--text-muted)' }} />
      </div>
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border rounded-xl shadow-xl overflow-hidden"
          style={{ borderColor: 'var(--border)' }}>
          <div className="px-3 py-2 border-b" style={{ borderColor: 'var(--border-light)' }}>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher..." autoFocus
              className="w-full text-xs focus:outline-none"
              style={{ color: 'var(--text)' }} />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0
              ? <p className="px-3 py-3 text-xs text-center" style={{ color: 'var(--text-placeholder)' }}>Aucun matériel</p>
              : filtered.map(m => {
                const active = selected.includes(m.materiel_id);
                return (
                  <button key={m.materiel_id} type="button"
                    onMouseDown={() => toggle(m.materiel_id)}
                    className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-gray-50"
                    style={{ borderBottom: '1px solid #F2F2EE' }}>
                    <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${active ? 'border-transparent' : ''}`}
                      style={{ background: active ? 'var(--primary-solid)' : 'white', borderColor: active ? 'var(--primary-solid)' : 'var(--border)' }}>
                      {active && <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 5l2.5 2.5 3.5-4" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" /></svg>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate" style={{ color: 'var(--text)' }}>{[m.marque, m.modele].filter(Boolean).join(' ') || m.materiel_id}</p>
                      {m.numero_serie && (
                        <p className="text-xs truncate" style={{ color: 'var(--text-placeholder)' }}>
                          {m.numero_serie}
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Financements() {
  const { isReadOnly, activeSociete, activeAgences } = useSociete();
  const navigate = useNavigate();
  const [financements, setFinancements] = useState([]);
  const [stats, setStats] = useState(null);
  const [materiels, setMateriels] = useState([]);
  const [organismes, setOrganismes] = useState([]);
  const [loading, setLoading] = useState(false);    // référentiel (matériels, organismes)
  const [loadingList, setLoadingList] = useState(true); // liste des financements
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [panelOpen, setPanelOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [onlyEnCours, setOnlyEnCours] = useState(false);
  const [selected, setSelected] = useState(new Set());

  // ── Document PDF ────────────────────────────────────────────────────────────
  const [docUrl, setDocUrl] = useState(null);
  const [docNom, setDocNom] = useState(null);

  // ── État export Odoo ────────────────────────────────────────────────────────
  const now = new Date();
  const [odooModal, setOdooModal]           = useState(false);
  const [odooMois, setOdooMois]             = useState(now.getMonth() + 1);
  const [odooAnnee, setOdooAnnee]           = useState(now.getFullYear());
  const [odooExporting, setOdooExporting]   = useState(false);
  const [odooResults, setOdooResults]       = useState(null);
  const [odooHistorique, setOdooHistorique] = useState({}); // { financement_id: [exports] }
  const [odooForce, setOdooForce]           = useState(new Set()); // ids forcés
  const [odooLoadingHisto, setOdooLoadingHisto] = useState(false);

  // Reset selection quand le filtre change
  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const allVisible = sorted => sorted.length > 0 && sorted.every(f => selected.has(f.financement_id));
  const someVisible = sorted => sorted.some(f => selected.has(f.financement_id));
  const toggleAll = (sorted) => {
    if (allVisible(sorted)) {
      setSelected(prev => { const next = new Set(prev); sorted.forEach(f => next.delete(f.financement_id)); return next; });
    } else {
      setSelected(prev => { const next = new Set(prev); sorted.forEach(f => next.add(f.financement_id)); return next; });
    }
  };

  const exportOdoo = useCallback(async () => {
    const rows = financements.filter(f => selected.has(f.financement_id));
    if (!rows.length) return;
    setOdooExporting(true);
    setOdooResults(null);
    const results = [];

    for (const row of rows) {
      const fid = row.financement_id;
      // Si déjà exporté ce mois et pas forcé → annuler d'abord
      const existingExport = (odooHistorique[fid] || [])
        .find(e => e.mois === odooMois && e.annee === odooAnnee);
      if (existingExport && odooForce.has(fid)) {
        try {
          await api.annulerExportOdoo(existingExport.export_id);
        } catch (err) {
          results.push({
            numero: row.numero_contrat,
            ok: false,
            message: `Impossible d'annuler la facture existante : ${err?.response?.data?.detail || err.message}`,
          });
          continue;
        }
      }
      try {
        const res = await api.exporterMensualiteOdoo(fid, { mois: odooMois, annee: odooAnnee });
        results.push({ numero: row.numero_contrat, financement_id: fid, ...res.data });
      } catch (err) {
        results.push({
          numero: row.numero_contrat,
          ok: false,
          message: err?.response?.data?.detail || 'Erreur inconnue',
        });
      }
    }
    setOdooResults(results);
    setOdooExporting(false);
    // Recharger l'historique
    loadOdooHistorique(rows.map(r => r.financement_id));
    const ok  = results.filter(r => r.ok).length;
    const err = results.filter(r => !r.ok).length;
    if (err === 0) toast.success(`${ok} facture${ok > 1 ? 's' : ''} envoyée${ok > 1 ? 's' : ''} vers Odoo`);
    else toast.error(`${err} erreur${err > 1 ? 's' : ''} · ${ok} succès`);
  }, [financements, selected, odooMois, odooAnnee, odooHistorique, odooForce]);

  const loadOdooHistorique = useCallback(async (ids) => {
    if (!ids || ids.length === 0) return;
    setOdooLoadingHisto(true);
    try {
      const results = await Promise.all(
        ids.map(id => api.getExportsOdoo(id).then(r => ({ id, data: r.data })).catch(() => ({ id, data: [] })))
      );
      setOdooHistorique(prev => {
        const next = { ...prev };
        results.forEach(({ id, data }) => { next[id] = data; });
        return next;
      });
    } finally {
      setOdooLoadingHisto(false);
    }
  }, []);

  // Charger l'historique quand la modale s'ouvre
  const openOdooModal = useCallback(() => {
    setOdooResults(null);
    setOdooForce(new Set());
    const ids = financements.filter(f => selected.has(f.financement_id)).map(f => f.financement_id);
    loadOdooHistorique(ids);
    setOdooModal(true);
  }, [financements, selected, loadOdooHistorique]);

  const toggleForce = useCallback((fid) => {
    setOdooForce(prev => {
      const next = new Set(prev);
      next.has(fid) ? next.delete(fid) : next.add(fid);
      return next;
    });
  }, []);

  // ── Export PDF ───────────────────────────────────────────────────────────────

  const exportPDF = useCallback(() => {
    const rows = financements.filter(f => selected.has(f.financement_id));
    if (!rows.length) return;

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const today = new Date().toLocaleDateString('fr-FR');

    // Titre
    doc.setFontSize(16);
    doc.setTextColor(26, 58, 110);
    doc.text('Contrats de financement', 14, 18);
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(`Exporté le ${today} · ${rows.length} contrat${rows.length > 1 ? 's' : ''} sélectionné${rows.length > 1 ? 's' : ''}`, 14, 25);

    autoTable(doc, {
      startY: 30,
      styles: { fontSize: 8, cellPadding: 2.5, overflow: 'linebreak' },
      headStyles: { fillColor: [26, 58, 110], textColor: 255, fontStyle: 'bold', fontSize: 8 },
      alternateRowStyles: { fillColor: [245, 247, 255] },
      columnStyles: { 0: { cellWidth: 32 }, 1: { cellWidth: 16 }, 2: { cellWidth: 35 } },
      head: [['N° Contrat', 'Type', 'Organisme', 'Matériels', 'Capital', 'Mensualité', 'Durée', 'Taux', 'Début', 'Fin']],
      body: rows.map(f => [
        f.numero_contrat || '—',
        f.type || '—',
        f.entreprise_nom || '—',
        (f.materiels_labels || []).map(m => m.label).join(', ') || '—',
        f.capital_emprunte ? `${fmt(f.capital_emprunte, 0)} €` : '—',
        f.mensualite ? `${fmt(f.mensualite, 0)} €` : '—',
        f.duree ? `${f.duree} mois` : '—',
        f.taux ? `${fmt(f.taux, 2)} %` : '—',
        fmtDate(f.date_debut) || '—',
        fmtDate(f.date_fin) || '—',
      ]),
      foot: [[
        { content: `Total : ${rows.length} contrat${rows.length > 1 ? 's' : ''}`, colSpan: 4, styles: { fontStyle: 'bold', fillColor: [240, 240, 240], textColor: 60 } },
        {
          content: `${fmt(rows.reduce((s, f) => s + (f.capital_emprunte || 0), 0), 0)} €`,
          styles: { fontStyle: 'bold', fillColor: [240, 240, 240], textColor: 26 }
        },
        {
          content: `${fmt(rows.reduce((s, f) => s + (f.mensualite || 0), 0), 0)} €/mois`,
          styles: { fontStyle: 'bold', fillColor: [240, 240, 240], textColor: 180 }
        },
        { content: '', colSpan: 4, styles: { fillColor: [240, 240, 240] } },
      ]],
      footStyles: { fontSize: 8 },
    });

    doc.save(`financements_${today.replace(/\//g, '-')}.pdf`);
    toast.success(`PDF généré — ${rows.length} contrat${rows.length > 1 ? 's' : ''}`);
  }, [selected, financements]);

  // ── Chargement ──────────────────────────────────────────────────────────────

  const _ctxKey = `${activeSociete?.societe_id || ''}|${activeAgences.map(a => a.agence_id).join(',')}`;

  const load = useCallback(() => {
    // ── Charger liste et référentiel en parallèle, indépendamment ───────────
    setLoadingList(true);
    setLoading(true);

    // Phase 1 : liste des financements (rapide, ~0.7s) → affichage immédiat
    Promise.all([
      api.getFinancements(),
      api.getFinancementsStats(),
    ]).then(([finRes, statsRes]) => {
      setFinancements(Array.isArray(finRes.data) ? finRes.data : []);
      setStats(statsRes.data);
      const ids = (Array.isArray(finRes.data) ? finRes.data : []).map(f => f.financement_id);
      if (ids.length > 0) loadOdooHistorique(ids);
    }).catch(() => toast.error('Erreur chargement financements'))
      .finally(() => setLoadingList(false));

    // Phase 2 : référentiel formulaire (lent, ~1.1s) → bouton désactivé pendant ce temps
    Promise.all([
      api.getMateriels({ slim: true }),
      api.getEntreprises({ type_entreprise: 'organisme_financier' }),
    ]).then(([matRes, entRes]) => {
      setMateriels(Array.isArray(matRes.data) ? matRes.data : []);
      setOrganismes(Array.isArray(entRes.data) ? entRes.data : []);
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, [_ctxKey]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // ── Calcul automatique ──────────────────────────────────────────────────────

  const applyAutoCalc = (f) => {
    const cap = parseFloat(f.capital_emprunte) || 0;
    const men = parseFloat(f.mensualite) || 0;
    const dur = parseInt(f.duree) || 0;
    const tau = parseFloat(f.taux) || 0;

    switch (f._deduire) {
      case 'capital_emprunte': {
        const v = calcCapital(men, dur, tau);
        return { ...f, capital_emprunte: v != null ? Number(v.toFixed(2)) : f.capital_emprunte };
      }
      case 'mensualite': {
        const v = calcMensualite(cap, dur, tau);
        return { ...f, mensualite: v != null ? Number(v.toFixed(2)) : f.mensualite };
      }
      case 'duree': {
        const v = calcDuree(cap, men, tau);
        return { ...f, duree: v != null ? v : f.duree };
      }
      case 'taux': {
        const v = calcTaux(cap, men, dur);
        return { ...f, taux: v != null ? Number(v.toFixed(4)) : f.taux };
      }
      default: return f;
    }
  };

  const setFormField = (field, value) => {
    setForm(f => {
      const updated = { ...f, [field]: value };
      // Recalculer capital depuis prix_bien et apports (sauf si capital est le champ déduit)
      if (['prix_bien', 'apport', 'apport_type', 'apport_snc', 'type'].includes(field)
        && updated._deduire !== 'capital_emprunte') {
        const prixBien = parseFloat(updated.prix_bien) || 0;
        let apportMontant = 0;
        if (updated.apport_type === 'montant') {
          apportMontant = parseFloat(updated.apport) || 0;
        } else {
          apportMontant = prixBien * ((parseFloat(updated.apport) || 0) / 100);
        }
        const apportSnc = (updated.type === 'SNC') ? (parseFloat(updated.apport_snc) || 0) : 0;
        if (prixBien > 0) {
          updated.capital_emprunte = Number((prixBien - apportMontant - apportSnc).toFixed(2));
        }
      }
      return applyAutoCalc(updated);
    });
  };

  const setDeduire = (field) => {
    setForm(f => applyAutoCalc({ ...f, _deduire: field }));
  };

  // ── Panel open/close ─────────────────────────────────────────────────────────

  const openNew = () => {
    setEditRow(null);
    setForm(EMPTY_FORM);
    setDocUrl(null);
    setDocNom(null);
    setPanelOpen(true);
  };

  const openEdit = (row) => {
    setEditRow(row);
    setDocUrl(row.document_url || null);
    setDocNom(row.document_nom || null);
    setForm({
      numero_contrat: row.numero_contrat || '',
      type: row.type || 'Leasing',
      entreprise_id: row.entreprise_id || '',
      materiel_ids: row.materiel_ids || [],
      prix_bien: row.prix_bien ?? '',
      apport: row.apport ?? '',
      apport_type: row.apport_type || 'montant',
      apport_snc: row.apport_snc ?? '',
      capital_emprunte: row.capital_emprunte ?? '',
      mensualite: row.mensualite ?? '',
      duree: row.duree ?? '',
      taux: row.taux ?? '',
      date_debut: row.date_debut || '',
      date_fin: row.date_fin || '',
      notes: row.notes || '',
      _deduire: 'mensualite',
    });
    setPanelOpen(true);
  };

  // ── CRUD ────────────────────────────────────────────────────────────────────

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce financement ?')) return;
    try {
      await api.deleteFinancement(id);
      toast.success('Financement supprimé');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleSave = async () => {
    if (!form.numero_contrat.trim()) { toast.error('Le numéro de contrat est obligatoire'); return; }
    setSaving(true);
    const payload = {
      numero_contrat: form.numero_contrat.trim(),
      type: form.type,
      entreprise_id: form.entreprise_id || null,
      materiel_ids: form.materiel_ids,
      prix_bien: parseFloat(form.prix_bien) || 0,
      apport: parseFloat(form.apport) || 0,
      apport_type: form.apport_type,
      apport_snc: (form.type === 'SNC' && form.apport_snc !== '') ? parseFloat(form.apport_snc) : null,
      capital_emprunte: parseFloat(form.capital_emprunte) || 0,
      mensualite: parseFloat(form.mensualite) || 0,
      duree: parseInt(form.duree) || 0,
      taux: parseFloat(form.taux) || 0,
      date_debut: form.date_debut || null,
      date_fin: form.date_fin || null,
      notes: form.notes || null,
    };
    try {
      if (editRow) {
        await api.updateFinancement(editRow.financement_id, payload);
        toast.success('Financement mis à jour');
      } else {
        await api.createFinancement(payload);
        toast.success('Financement créé');
      }
      setPanelOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  // ── Tri / filtrage ──────────────────────────────────────────────────────────

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const getSortVal = (row, col) => {
    switch (col) {
      case 'numero_contrat': return row.numero_contrat || '';
      case 'type': return row.type || '';
      case 'entreprise': return row.entreprise_nom || '';
      case 'capital_emprunte': return row.capital_emprunte || 0;
      case 'mensualite': return row.mensualite || 0;
      case 'duree': return row.duree || 0;
      case 'taux': return row.taux || 0;
      case 'date_debut': return row.date_debut || '';
      case 'date_fin': return row.date_fin || '';
      default: return row.created_at || '';
    }
  };

  const filtered = financements.filter(f => {
    if (onlyEnCours) {
      const today = new Date().toISOString().split('T')[0];
      const debutOk = !f.date_debut || f.date_debut <= today;
      const finOk   = !f.date_fin   || f.date_fin   >= today;
      if (!debutOk || !finOk) return false;
    }
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (f.numero_contrat || '').toLowerCase().includes(q) ||
      (f.type || '').toLowerCase().includes(q) ||
      (f.entreprise_nom || '').toLowerCase().includes(q) ||
      (f.materiels_labels || []).some(m => (m.label || '').toLowerCase().includes(q))
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    const va = getSortVal(a, sortCol), vb = getSortVal(b, sortCol);
    if (typeof va === 'number' && typeof vb === 'number')
      return sortDir === 'asc' ? va - vb : vb - va;
    return sortDir === 'asc'
      ? String(va).localeCompare(String(vb), 'fr')
      : String(vb).localeCompare(String(va), 'fr');
  });

  // ── Styles ──────────────────────────────────────────────────────────────────

  const inp = "w-full px-3 py-2 rounded-lg border text-sm focus:outline-none";
  const inpSt = { borderColor: 'var(--border)' };
  const lbl = "block text-xs font-semibold uppercase tracking-wider mb-1";
  const lblSt = { color: 'var(--text-muted)',  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronsUpDown size={12} style={{ opacity: 0.3 }} />;
    return sortDir === 'asc' ? <ChevronUp size={12} style={{ color: 'var(--primary-solid)' }} /> : <ChevronDown size={12} style={{ color: 'var(--primary-solid)' }} />;
  };

  const thCls = "px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap cursor-pointer select-none";
  const thSt = { color: 'var(--text-muted)' };

  // ── Champ déduit (section radio) ────────────────────────────────────────────

  const DEDUIRE_FIELDS = [
    { key: 'capital_emprunte', label: 'Capital emprunté', unit: '€' },
    { key: 'mensualite', label: 'Mensualité', unit: '€/mois' },
    { key: 'duree', label: 'Durée', unit: 'mois' },
    { key: 'taux', label: 'Taux annuel', unit: '%' },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loadingList) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full"
          style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <ReadOnlyBanner />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-solid)',  }}>Financements</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>Gérez vos contrats de financement (Leasing, SNC)</p>
        </div>
        <button onClick={openNew} data-testid="add-financement-btn"
          disabled={isReadOnly || loading}
          title={isReadOnly ? 'Mode lecture seule' : loading ? 'Chargement du référentiel...' : undefined}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: 'var(--primary-gradient)' }}>
          {loading ? <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : isReadOnly ? <Lock size={16} /> : <Plus size={16} />} Nouveau financement
        </button>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard
            title="Capital total financé"
            value={`${fmt(stats.capital_total || 0, 0)} €`}
            accent="var(--primary-solid)"
          />
          <StatCard
            title="Contrats en cours"
            value={stats.nb_actifs || 0}
            sub={`sur ${stats.nb_total || 0} au total`}
            accent="#15803D"
            active={onlyEnCours}
            onToggle={() => setOnlyEnCours(v => !v)}
          />
          <StatCard
            title="Mensualités / mois"
            value={`${fmt(stats.mensualites_totales || 0, 0)} €`}
            accent="#B45309"
          />
          <StatCard
            title="Répartition"
            value={`${stats.nb_leasing || 0} Leasing · ${stats.nb_snc || 0} SNC`}
            sub="Leasing · SNC"
            accent="#1D4ED8"
          />
        </div>
      )}

      {/* Recherche + Export */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-80">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-placeholder)' }} />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher N°, organisme, matériel..."
            data-testid="search-financements"
            className="w-full pl-9 pr-4 py-2 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border)', background: 'white' }} />
        </div>

        {selected.size > 0 && (
          <button
            data-testid="export-pdf-btn"
            onClick={exportPDF}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all border hover:shadow-md"
            style={{ background: '#1A3A6E', color: 'white', borderColor: '#1A3A6E' }}
          >
            <FileDown size={14} />
            Exporter PDF
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold"
              style={{ background: 'rgba(255,255,255,0.25)' }}>{selected.size}</span>
          </button>
        )}
        {selected.size > 0 && (
          <button
            data-testid="export-odoo-btn"
            onClick={openOdooModal}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all border hover:shadow-md"
            style={{ background: '#7C3AED', color: 'white', borderColor: '#7C3AED' }}
          >
            <Send size={14} />
            Exporter vers Odoo
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold"
              style={{ background: 'rgba(255,255,255,0.25)' }}>
              {selected.size}
            </span>
          </button>
        )}
        {selected.size > 0 && (
          <button
            onClick={() => setSelected(new Set())}
            className="text-xs px-2 py-1.5 rounded-lg border transition-colors hover:bg-slate-50"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
          >
            Désélectionner
          </button>
        )}
      </div>

      {/* Table */}
      {sorted.length === 0 ? (
        <div className="rounded-xl py-16 text-center" style={{ border: '1px dashed #E6E6E1' }}>
          <CreditCard size={36} className="mx-auto mb-3" style={{ color: 'var(--text-disabled)' }} />
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
            {search ? 'Aucun résultat' : 'Aucun financement'}
          </p>
          {!search && (
            <p className="text-xs mb-4" style={{ color: 'var(--text-placeholder)' }}>
              Créez votre premier contrat avec le bouton "Nouveau financement"
            </p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid #E6E6E1' }}>
                  {/* Checkbox select all */}
                  <th className="px-3 py-2.5 w-10" style={{ ...thSt, cursor: 'default' }}
                    onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      data-testid="select-all-checkbox"
                      checked={allVisible(sorted)}
                      ref={el => { if (el) el.indeterminate = someVisible(sorted) && !allVisible(sorted); }}
                      onChange={() => toggleAll(sorted)}
                      className="w-4 h-4 rounded cursor-pointer accent-blue-700"
                    />
                  </th>
                  <th onClick={() => handleSort('numero_contrat')} className={thCls} style={thSt}>
                    <span className="inline-flex items-center gap-1">N° contrat <SortIcon col="numero_contrat" /></span>
                  </th>
                  <th onClick={() => handleSort('type')} className={thCls} style={thSt}>
                    <span className="inline-flex items-center gap-1">Type <SortIcon col="type" /></span>
                  </th>
                  <th onClick={() => handleSort('entreprise')} className={thCls} style={thSt}>
                    <span className="inline-flex items-center gap-1">Organisme <SortIcon col="entreprise" /></span>
                  </th>
                  <th className={thCls} style={{ ...thSt, cursor: 'default' }}>Matériels</th>
                  <th onClick={() => handleSort('capital_emprunte')} className={thCls} style={thSt}>
                    <span className="inline-flex items-center gap-1">Capital <SortIcon col="capital_emprunte" /></span>
                  </th>
                  <th onClick={() => handleSort('mensualite')} className={thCls} style={thSt}>
                    <span className="inline-flex items-center gap-1">Mensualité <SortIcon col="mensualite" /></span>
                  </th>
                  <th onClick={() => handleSort('duree')} className={thCls} style={thSt}>
                    <span className="inline-flex items-center gap-1">Durée <SortIcon col="duree" /></span>
                  </th>
                  <th onClick={() => handleSort('taux')} className={thCls} style={thSt}>
                    <span className="inline-flex items-center gap-1">Taux <SortIcon col="taux" /></span>
                  </th>
                  <th onClick={() => handleSort('date_debut')} className={thCls} style={thSt}>
                    <span className="inline-flex items-center gap-1">Début <SortIcon col="date_debut" /></span>
                  </th>
                  <th onClick={() => handleSort('date_fin')} className={thCls} style={thSt}>
                    <span className="inline-flex items-center gap-1">Fin <SortIcon col="date_fin" /></span>
                  </th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide" style={thSt}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((f) => {
                  const isSelected = selected.has(f.financement_id);
                  return (
                  <tr key={f.financement_id} data-testid="financement-row"
                    onClick={() => openEdit(f)}
                    className="hover:bg-blue-50/40 transition-colors cursor-pointer"
                    style={{
                      borderBottom: '1px solid var(--border-light)',
                      background: isSelected ? '#EFF6FF' : undefined,
                    }}>
                    {/* Checkbox */}
                    <td className="px-3 py-2.5 w-10" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        data-testid={`select-row-${f.financement_id}`}
                        checked={isSelected}
                        onChange={() => toggleSelect(f.financement_id)}
                        className="w-4 h-4 rounded cursor-pointer accent-blue-700"
                      />
                    </td>
                    {/* N° contrat */}
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono px-2 py-0.5 rounded font-semibold text-sm"
                          style={{ background: 'var(--border-light)', color: 'var(--text-secondary)' }}>
                          {f.numero_contrat}
                        </span>
                        {(odooHistorique[f.financement_id] || []).length > 0 && (
                          <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
                            style={{ background: '#EDE9FE', color: '#6D28D9' }}
                            title={`Exporté vers Odoo — ${(odooHistorique[f.financement_id] || []).map(e => `${e.mois}/${e.annee}: ${e.odoo_invoice_name}`).join(', ')}`}>
                            Odoo
                          </span>
                        )}
                      </div>
                    </td>
                    {/* Type */}
                    <td className="px-3 py-2.5"><TypeBadge type={f.type} /></td>
                    {/* Organisme */}
                    <td className="px-3 py-2.5">
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {f.entreprise_nom || <span style={{ color: 'var(--text-disabled)' }}>—</span>}
                      </span>
                    </td>
                    {/* Matériels */}
                    <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                      {(f.materiels_labels || []).length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {(f.materiels_labels || []).slice(0, 2).map((m) => (
                            <button
                              key={m.materiel_id}
                              type="button"
                              onClick={() => navigate(`/materiels?materiel=${m.materiel_id}`)}
                              className="text-xs px-1.5 py-0.5 rounded hover:underline transition-colors"
                              style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}
                              title="Ouvrir la fiche matériel"
                            >
                              {m.label}
                            </button>
                          ))}
                          {(f.materiels_labels || []).length > 2 && (
                            <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#F3F4F6', color: '#6B7280' }}>
                              +{f.materiels_labels.length - 2}
                            </span>
                          )}
                        </div>
                      ) : <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>—</span>}
                    </td>
                    {/* Capital */}
                    <td className="px-3 py-2.5">
                      <span className="text-xs font-semibold whitespace-nowrap" style={{ color: 'var(--primary-solid)' }}>
                        {f.capital_emprunte ? `${fmt(f.capital_emprunte, 0)} €` : '—'}
                      </span>
                    </td>
                    {/* Mensualité */}
                    <td className="px-3 py-2.5">
                      <span className="text-xs whitespace-nowrap" style={{ color: '#B45309' }}>
                        {f.mensualite ? `${fmt(f.mensualite, 0)} €` : '—'}
                      </span>
                    </td>
                    {/* Durée */}
                    <td className="px-3 py-2.5">
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {f.duree ? `${f.duree} mois` : '—'}
                      </span>
                    </td>
                    {/* Taux */}
                    <td className="px-3 py-2.5">
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {f.taux ? `${fmt(f.taux, 2)} %` : '—'}
                      </span>
                    </td>
                    {/* Date début */}
                    <td className="px-3 py-2.5">
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{fmtDate(f.date_debut)}</span>
                    </td>
                    {/* Date fin */}
                    <td className="px-3 py-2.5">
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{fmtDate(f.date_fin)}</span>
                    </td>
                    {/* Actions */}
                    <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => handleDelete(f.financement_id)}
                          data-testid={`delete-financement-${f.financement_id}`}
                          className="p-1.5 rounded hover:bg-red-50 transition-colors"
                          style={{ color: '#DC2626' }}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2.5 border-t text-xs flex items-center justify-between" style={{ borderColor: 'var(--border)', color: 'var(--text-placeholder)' }}>
            <span>{sorted.length} contrat{sorted.length !== 1 ? 's' : ''}</span>
            {onlyEnCours && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
                style={{ background: '#DCFCE7', color: '#15803D' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Filtre actif : contrats en cours
              </span>
            )}
          </div>
        </div>
      )}

      {/* ─── Side Panel ──────────────────────────────────────────────────────── */}
      <SidePanel
        open={panelOpen}
        onOpenChange={o => { if (!o) setPanelOpen(false); }}
        size="lg"
        title={editRow ? editRow.numero_contrat : 'Nouveau financement'}
        copyValue={editRow?.numero_contrat}
        subtitle={editRow ? 'Contrat de financement' : 'Créer un contrat de financement'}
        footer={
          <div className="flex items-center justify-between">
            {editRow ? (
              <button type="button" onClick={() => { setPanelOpen(false); handleDelete(editRow.financement_id); }}
                disabled={isReadOnly}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ color: '#DC2626' }}>
                <Trash2 size={14} /> Supprimer
              </button>
            ) : <span />}
            <div className="flex gap-3">
              <button type="button" onClick={() => setPanelOpen(false)}
                className="px-4 py-2.5 rounded-lg text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                Annuler
              </button>
              <button data-testid="save-financement-btn" type="button" onClick={handleSave}
                disabled={saving || isReadOnly}
                className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: (saving || isReadOnly) ? 'var(--primary-light)' : 'var(--primary-gradient)' }}>
                {saving ? 'Enregistrement...' : (editRow ? 'Mettre à jour' : 'Créer')}
              </button>
            </div>
          </div>
        }
      >
        <div className="space-y-4">

          {/* Type */}
          <div>
            <label className={lbl} style={lblSt}>Type de financement</label>
            <div className="flex gap-2">
              {['Leasing', 'SNC'].map(t => {
                const c = TYPE_COLORS[t];
                return (
                  <button key={t} type="button"
                    onClick={() => setFormField('type', t)}
                    data-testid={`type-${t}`}
                    className="flex-1 py-2.5 rounded-lg text-sm font-semibold border transition-all"
                    style={{
                      background: form.type === t ? c.bg : 'white',
                      color: form.type === t ? c.color : 'var(--text-muted)',
                      borderColor: form.type === t ? c.border : 'var(--border)',
                    }}>
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

          {/* N° contrat | Organisme — 2 colonnes */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl} style={lblSt}>N° de contrat <span style={{ color: '#DC2626' }}>*</span></label>
              <input type="text" value={form.numero_contrat}
                onChange={e => setFormField('numero_contrat', e.target.value)}
                placeholder="Ex. : LSG-2025-0042"
                data-testid="form-numero-contrat"
                className={inp} style={inpSt} />
            </div>
            <div>
              <label className={lbl} style={lblSt}>Organisme financier</label>
              {organismes.length === 0 ? (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs"
                  style={{ background: '#FEF9C3', color: '#B45309', border: '1px solid #FDE68A' }}>
                  <AlertCircle size={13} />
                  Aucun organisme financier dans l'Annuaire
                </div>
              ) : (
                <select value={form.entreprise_id}
                  onChange={e => setFormField('entreprise_id', e.target.value)}
                  data-testid="form-entreprise"
                  className={inp} style={inpSt}>
                  <option value="">— Sélectionner —</option>
                  {organismes.map(o => (
                    <option key={o.entreprise_id} value={o.entreprise_id}>{o.nom}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Matériels associés — pleine largeur */}
          <div>
            <label className={lbl} style={lblSt}>Matériels associés</label>
            <MaterielsMultiSelect
              selected={form.materiel_ids}
              materiels={materiels}
              onChange={ids => setFormField('materiel_ids', ids)}
              onNavigate={id => { setPanelOpen(false); navigate(`/materiels?materiel=${id}`); }}
            />
          </div>

          {/* ── Section financière ── */}
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: '#E0E7FF' }}>
            <div className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider"
              style={{ background: '#EEF2FF', color: '#4338CA' }}>
              Détail financier
            </div>
            <div className="px-4 py-4 space-y-4" style={{ background: 'white' }}>

              {/* Prix du bien | Apport — 2 colonnes, inputs alignés */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  {/* Même hauteur de header que la case Apport */}
                  <div className="flex items-center mb-1" style={{ minHeight: '22px' }}>
                    <label className={lbl} style={{ ...lblSt, marginBottom: 0 }}>Prix du bien (€)</label>
                  </div>
                  <div className="relative">
                    <PriceInput value={form.prix_bien}
                      onChange={v => setFormField('prix_bien', v)}
                      placeholder="0,00"
                      data-testid="form-prix-bien"
                      className={`${inp} pr-8`} style={inpSt} />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium pointer-events-none" style={{ color: 'var(--text-placeholder)' }}>€</span>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1" style={{ minHeight: '22px' }}>
                    <label className={lbl} style={{ ...lblSt, marginBottom: 0 }}>Apport</label>
                    <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border)' }}>
                      <button type="button"
                        onClick={() => setFormField('apport_type', 'montant')}
                        className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium transition-colors"
                        style={{
                          background: form.apport_type === 'montant' ? 'var(--primary-solid)' : 'white',
                          color: form.apport_type === 'montant' ? 'white' : 'var(--text-muted)',
                        }}>
                        <Hash size={10} /> €
                      </button>
                      <button type="button"
                        onClick={() => setFormField('apport_type', 'pourcentage')}
                        className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium transition-colors"
                        style={{
                          background: form.apport_type === 'pourcentage' ? 'var(--primary-solid)' : 'white',
                          color: form.apport_type === 'pourcentage' ? 'white' : 'var(--text-muted)',
                        }}>
                        <Percent size={10} /> %
                      </button>
                    </div>
                  </div>
                  <div className="relative">
                    {form.apport_type === 'montant' ? (
                      <PriceInput value={form.apport}
                        onChange={v => setFormField('apport', v)}
                        placeholder="0,00"
                        data-testid="form-apport"
                        className={`${inp} pr-8`} style={inpSt} />
                    ) : (
                      <input type="number" min="0" max="100" step="0.1" value={form.apport}
                        onChange={e => setFormField('apport', e.target.value)}
                        placeholder="0"
                        data-testid="form-apport"
                        className={`${inp} pr-8`} style={inpSt} />
                    )}
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium pointer-events-none" style={{ color: 'var(--text-placeholder)' }}>
                      {form.apport_type === 'pourcentage' ? '%' : '€'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Apport SNC — conditionnel, pleine largeur */}
              {form.type === 'SNC' && (
                <div>
                  <label className={lbl} style={lblSt}>Apport SNC (€)</label>
                  <div className="relative">
                    <PriceInput value={form.apport_snc}
                      onChange={v => setFormField('apport_snc', v)}
                      placeholder="0,00"
                      data-testid="form-apport-snc"
                      className={`${inp} pr-8`} style={{ borderColor: '#BBF7D0', background: '#F0FDF4' }} />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium pointer-events-none" style={{ color: '#15803D' }}>€</span>
                  </div>
                </div>
              )}

              {/* Section calcul — 4 champs en grille 2×2 */}
              <div className="rounded-xl border overflow-hidden" style={{ borderColor: '#FDE68A' }}>
                <div className="px-3 py-2 flex items-center gap-2"
                  style={{ background: '#FEF9C3' }}>
                  <Calculator size={13} style={{ color: '#B45309' }} />
                  <span className="text-xs font-semibold" style={{ color: '#B45309' }}>
                    Sélectionnez le champ à calculer automatiquement
                  </span>
                </div>
                <div className="grid grid-cols-2" style={{ background: 'white' }}>
                  {DEDUIRE_FIELDS.map(({ key, label, unit }, idx) => {
                    const isDeduire = form._deduire === key;
                    const borderRight = idx % 2 === 0;
                    const borderBottom = idx < 2;
                    return (
                      <div key={key}
                        className="p-3"
                        style={{
                          background: isDeduire ? '#FFFBEB' : 'white',
                          borderRight: borderRight ? '1px solid #FEF9C3' : 'none',
                          borderBottom: borderBottom ? '1px solid #FEF9C3' : 'none',
                        }}>
                        {/* Radio + label */}
                        <div className="flex items-center gap-2 mb-2">
                          <button type="button"
                            onClick={() => setDeduire(key)}
                            data-testid={`radio-${key}`}
                            className="flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all"
                            style={{
                              borderColor: isDeduire ? '#B45309' : 'var(--border)',
                              background: isDeduire ? '#B45309' : 'white',
                            }}>
                            {isDeduire && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                          </button>
                          <label className="text-xs font-semibold leading-tight"
                            style={{ color: isDeduire ? '#B45309' : 'var(--text-muted)',  }}>
                            {label}
                          </label>
                        </div>
                        {/* Input */}
                        <div className="relative">
                          {['capital_emprunte', 'mensualite'].includes(key) && !isDeduire ? (
                            <PriceInput
                              value={form[key]}
                              onChange={v => setFormField(key, v)}
                              disabled={isDeduire}
                              data-testid={`form-${key}`}
                              placeholder="0,00"
                              className="w-full px-3 py-1.5 rounded-lg border text-sm text-right focus:outline-none transition-colors pr-9"
                              style={{
                                borderColor: 'var(--border)',
                                background: 'white',
                                color: 'var(--text)',
                              }}
                            />
                          ) : (
                            <input
                              type={isDeduire ? 'text' : 'number'}
                              min="0"
                              step={key === 'duree' ? '1' : '0.01'}
                              value={isDeduire && ['capital_emprunte', 'mensualite'].includes(key)
                                ? (form[key] !== '' && form[key] != null ? (() => {
                                    const n = parseFloat(form[key]);
                                    return isNaN(n) ? '' : n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/[\u00A0\u202F ]/g, '.');
                                  })() : '')
                                : form[key]}
                              onChange={e => setFormField(key, e.target.value)}
                              disabled={isDeduire}
                              data-testid={`form-${key}`}
                              placeholder={isDeduire ? 'calculé auto.' : '0'}
                              readOnly={isDeduire}
                              className="w-full px-3 py-1.5 rounded-lg border text-sm text-right focus:outline-none transition-colors pr-9"
                              style={{
                                borderColor: isDeduire ? '#FDE68A' : 'var(--border)',
                                background: isDeduire ? '#FEF9C3' : 'white',
                                color: isDeduire ? '#B45309' : 'var(--text)',
                                fontStyle: isDeduire ? 'italic' : 'normal',
                              }}
                            />
                          )}
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs pointer-events-none"
                            style={{ color: isDeduire ? '#B45309' : 'var(--text-placeholder)' }}>
                            {unit}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Dates — 2 colonnes (déjà) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl} style={lblSt}>Date de début</label>
              <input type="date" value={form.date_debut}
                onChange={e => setFormField('date_debut', e.target.value)}
                data-testid="form-date-debut"
                className={inp} style={inpSt} />
            </div>
            <div>
              <label className={lbl} style={lblSt}>Date de fin</label>
              <input type="date" value={form.date_fin}
                onChange={e => setFormField('date_fin', e.target.value)}
                data-testid="form-date-fin"
                className={inp} style={inpSt} />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className={lbl} style={lblSt}>Notes</label>
            <textarea value={form.notes}
              onChange={e => setFormField('notes', e.target.value)}
              rows={2} placeholder="Observations, conditions particulières..."
              className={inp} style={{ ...inpSt, resize: 'none' }} />
          </div>

          {/* Document PDF — uniquement sur une fiche existante */}
          {editRow && (
            <PdfDropZone
              financementId={editRow.financement_id}
              docUrl={docUrl}
              docNom={docNom}
              readOnly={isReadOnly}
              onUploaded={(url, nom) => { setDocUrl(url); setDocNom(nom); }}
              onDeleted={() => { setDocUrl(null); setDocNom(null); }}
            />
          )}

          {/* Avertissement champ manquant */}
          {!form.numero_contrat.trim() && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs" style={{ background: '#FEF9C3', color: '#B45309' }}>
              <AlertCircle size={13} />
              Le numéro de contrat est obligatoire.
            </div>
          )}
        </div>
      </SidePanel>

      {/* ── Modale export Odoo ─────────────────────────────────────────────── */}
      {odooModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={e => { if (e.target === e.currentTarget && !odooExporting) setOdooModal(false); }}>
          <div className="w-full max-w-md mx-4 rounded-2xl shadow-2xl p-6 space-y-5"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>

            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-bold" style={{ color: 'var(--primary-solid)',  }}>
                  Exporter vers Odoo
                </h2>
                <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  {selected.size} contrat{selected.size > 1 ? 's' : ''} sélectionné{selected.size > 1 ? 's' : ''}
                </p>
              </div>
              {!odooExporting && (
                <button onClick={() => setOdooModal(false)}
                  className="p-1.5 rounded-lg hover:bg-slate-100" style={{ color: 'var(--text-muted)' }}>
                  <X size={16} />
                </button>
              )}
            </div>

            {!odooResults ? (
              <>
                {/* Sélection du mois */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Mois</label>
                    <select value={odooMois} onChange={e => setOdooMois(parseInt(e.target.value))}
                      data-testid="odoo-mois-select"
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      style={{ borderColor: 'var(--border)' }}>
                      {['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre']
                        .map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Année</label>
                    <select value={odooAnnee} onChange={e => setOdooAnnee(parseInt(e.target.value))}
                      data-testid="odoo-annee-select"
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      style={{ borderColor: 'var(--border)' }}>
                      {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </div>

                {/* Récap contrats avec statut d'export */}
                <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border)' }}>
                  <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider flex items-center justify-between"
                    style={{ background: 'var(--surface-secondary)', color: 'var(--text-muted)' }}>
                    <span>Contrats à exporter</span>
                    {odooLoadingHisto && <Loader2 size={11} className="animate-spin" />}
                  </div>
                  <div className="divide-y" style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {financements.filter(f => selected.has(f.financement_id)).map(f => {
                      const fid = f.financement_id;
                      const existingExport = (odooHistorique[fid] || [])
                        .find(e => e.mois === odooMois && e.annee === odooAnnee);
                      const isForced = odooForce.has(fid);
                      return (
                        <div key={fid} className="px-3 py-2.5">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
                              {f.numero_contrat}
                            </span>
                            <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
                              {f.mensualite ? `${fmt(f.mensualite, 0)} €` :
                                <span className="text-amber-600 text-xs font-semibold">Mensualité manquante</span>}
                            </span>
                          </div>
                          {existingExport ? (
                            <div className="flex items-center justify-between mt-1.5 gap-2">
                              <div className="flex items-center gap-1.5">
                                <CheckCircle2 size={12} style={{ color: '#16A34A', flexShrink: 0 }} />
                                <span className="text-xs" style={{ color: '#16A34A' }}>
                                  Déjà exporté — {existingExport.odoo_invoice_name}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => toggleForce(fid)}
                                data-testid={`force-reimport-${fid}`}
                                className="text-xs px-2 py-0.5 rounded border transition-colors"
                                style={isForced
                                  ? { background: '#FEF2F2', color: '#DC2626', borderColor: '#FECACA' }
                                  : { background: 'var(--surface-secondary)', color: 'var(--text-muted)', borderColor: 'var(--border)' }}>
                                {isForced ? '↩ Annuler' : '⟳ Réimporter'}
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 mt-1">
                              <div className="w-2 h-2 rounded-full" style={{ background: '#6D28D9' }} />
                              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Pas encore exporté ce mois</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Info comptes */}
                <div className="text-xs px-3 py-2.5 rounded-lg" style={{ background: '#F0F4FF', color: '#1D4ED8' }}>
                  Compte charge : 612200 · TVA : 8,5% · Compte fournisseur : 401100
                </div>

                <button
                  data-testid="odoo-confirmer-export-btn"
                  onClick={exportOdoo}
                  disabled={odooExporting}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg, #7C3AED, #6D28D9)' }}>
                  {odooExporting ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                  {odooExporting ? 'Export en cours…' :
                    odooForce.size > 0 ? `Envoyer (${odooForce.size} réimportation${odooForce.size > 1 ? 's' : ''})` :
                    'Envoyer vers Odoo'}
                </button>
              </>
            ) : (
              <>
                {/* Résultats */}
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {odooResults.map((r, i) => (
                    <div key={i} className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg text-sm"
                      style={{ background: r.ok ? '#F0FDF4' : '#FEF2F2', border: `1px solid ${r.ok ? '#BBF7D0' : '#FECACA'}` }}>
                      {r.ok
                        ? <CheckCircle2 size={15} style={{ color: '#16A34A', flexShrink: 0, marginTop: 1 }} />
                        : <AlertCircle size={15} style={{ color: '#DC2626', flexShrink: 0, marginTop: 1 }} />
                      }
                      <div className="flex-1 min-w-0">
                        <p className="font-medium" style={{ color: r.ok ? '#15803D' : '#B91C1C' }}>
                          {r.numero} — {r.ok ? (r.export?.odoo_invoice_name || 'Créée') : 'Erreur'}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: r.ok ? '#166534' : '#991B1B', opacity: 0.85 }}>
                          {r.message}
                        </p>
                        {r.ok && r.export?.montant_ttc && (
                          <p className="text-xs mt-0.5 font-semibold" style={{ color: '#166534' }}>
                            HT {fmt(r.export.montant, 2)} € · TVA {fmt(r.export.montant_tva, 2)} € · TTC {fmt(r.export.montant_ttc, 2)} €
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => { setOdooModal(false); setOdooResults(null); }}
                  className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold border"
                  style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>
                  Fermer
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
